import { useLang } from "../lib/i18n";
import useReveal from "../hooks/useReveal";

export const Services = () => {
    const { t } = useLang();
    const { ref, visible } = useReveal();

    return (
        <section
            id="services"
            ref={ref}
            className={`relative py-24 md:py-32 border-t border-navy/10 reveal ${
                visible ? "visible" : ""
            }`}
            data-testid="services-section"
        >
            {/* Soft warm background wash */}
            <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-warm/10 blur-3xl pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-16">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-navy/60">
                            <span className="tick" />
                            {t.services.kicker}
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
                            data-testid="services-heading"
                        >
                            {t.services.heading}
                        </h2>
                        <p className="mt-6 text-navy/70 text-lg max-w-2xl leading-relaxed">
                            {t.services.subheading}
                        </p>
                    </div>
                </div>

                {/* Grid of cards with images */}
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                    data-testid="services-grid"
                >
                    {t.services.items.map((item, i) => (
                        <article
                            key={i}
                            className="group relative bg-cream border border-navy/15 hover:border-warm transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(10,25,47,0.25)] overflow-hidden"
                            data-testid={`service-${i}`}
                        >
                            {/* Image */}
                            <div className="relative aspect-[4/3] overflow-hidden bg-cream-200">
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    data-testid={`service-${i}-image`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-navy/10 to-transparent" />
                                <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-warm text-navy flex items-center justify-center font-display text-lg shadow-md">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-6 md:p-7">
                                <h3 className="font-display text-2xl text-navy group-hover:text-warm-600 transition-colors">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-navy/70 text-[15px] leading-relaxed">
                                    {item.copy}
                                </p>
                                <div className="mt-5 flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-[0.2em] text-navy/50">
                                        {String(i + 1).padStart(2, "0")} / 06
                                    </span>
                                    <span className="text-navy/50 group-hover:text-warm-600 group-hover:translate-x-1 transition-all text-lg">
                                        →
                                    </span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Services;
